import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import Caching from "../../../CacheManager";
import { FileUpload } from "../../../components/inputs/types";
import { ZIP_EXTENSION, ZIP_MIME_TYPES } from "../../../config";
import { useApi } from "../../../contexts/ApiContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useError } from "../../../contexts/ErrorContext";
import {
  CaseFile,
  CreateCaseError,
  CreateCaseRequest,
  CreateCaseResponse,
  CreateCaseRevisionRequest,
  CreateCaseRevisionResponse,
  CreateRevisionError,
  FileUploadError,
  RevisionFilesPresignResponse,
  UPLOAD_ERROR_CODE_SET,
} from "../types";
import FileUploader from "../../../FileUploader";
import { presignedUrlsToMap } from "../utils";

type UploadProgressCallback = (file: File, progress: number) => void;

export const isZipFile = (f: File): boolean =>
  ZIP_MIME_TYPES.includes(f.type) ||
  f.name.toLowerCase().endsWith(ZIP_EXTENSION);

const isNewZipUpload = (f: FileUpload | CaseFile): f is FileUpload =>
  "file" in f && isZipFile(f.file);

const collectZipOutcomes = (
  zipResults: PromiseSettledResult<void>[]
): { zipErrorMessage?: string } => {
  const zipErrors: string[] = [];
  for (const r of zipResults) {
    if (r.status === "rejected") {
      zipErrors.push(
        r.reason instanceof Error ? r.reason.message : String(r.reason)
      );
    }
  }
  return {
    zipErrorMessage: zipErrors.length > 0 ? zipErrors.join("; ") : undefined,
  };
};

const GCS_PUT_CONTENT_TYPE = "application/zip";

// Retry transient PUT failures (network drops after body send, 5xx) a few
// times before giving up. Don't retry 4xx — those are deterministic
// (signature mismatch, expired URL, bad Content-Type).
const MAX_PUT_ATTEMPTS = 3;

type TransientError = Error & { transient?: boolean };

export const useZipUpload = () => {
  const { client } = useApi();

  const putToGcsOnce = (
    url: string,
    file: File,
    onProgress?: UploadProgressCallback
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      // The GCS signed URL is signed with a specific Content-Type; if the
      // request's Content-Type doesn't match, GCS returns 403
      // SignatureDoesNotMatch. Set it explicitly instead of relying on the
      // browser's auto-detected MIME (which varies by OS for .zip).
      xhr.setRequestHeader("Content-Type", GCS_PUT_CONTENT_TYPE);
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(file, Math.round((e.loaded / e.total) * 100));
          }
        };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(file, 100);
          resolve();
        } else {
          const err: TransientError = new Error(
            `GCS PUT failed: ${xhr.status}`
          );
          err.transient = xhr.status >= 500;
          reject(err);
        }
      };
      xhr.onerror = () => {
        const err: TransientError = new Error("GCS PUT network error");
        err.transient = true;
        reject(err);
      };
      xhr.send(file);
    });
  };

  const putToGcs = async (
    url: string,
    file: File,
    onProgress?: UploadProgressCallback
  ): Promise<void> => {
    for (let attempt = 1; attempt <= MAX_PUT_ATTEMPTS; attempt++) {
      try {
        await putToGcsOnce(url, file, onProgress);
        return;
      } catch (err) {
        const transient = (err as TransientError).transient;
        if (!transient || attempt === MAX_PUT_ATTEMPTS) {
          // Log the raw error for debugging, then surface a friendly message.
          console.error(
            `Zip upload failed for "${file.name}" after ${attempt} attempt(s):`,
            err
          );
          throw new FileUploadError(
            transient
              ? `Couldn't upload "${file.name}". Please check your internet connection and try again.`
              : `Couldn't upload "${file.name}". Please try again.`
          );
        }
        // Reset progress so the UI doesn't stay pinned at the pre-failure
        // byte count while we wait to retry.
        onProgress?.(file, 0);
        // Exponential backoff with jitter: ~500ms, ~1500ms.
        const delay = 500 * 2 ** (attempt - 1) + Math.random() * 250;
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  };

  const uploadZip = async (
    caseId: string,
    revisionId: number,
    file: File,
    onProgress?: UploadProgressCallback
  ): Promise<void> => {
    const initResponse = await client.POST(
      "/v1/cases/{case_id}/revisions/{revision_id}/uploads/zip/init",
      {
        params: {
          path: { case_id: caseId, revision_id: revisionId },
        },
        body: { filename: file.name, length: file.size },
      }
    );

    if (initResponse.error || !initResponse.data) {
      throw new Error(`${file.name}: Failed to initialize zip upload`);
    }

    const { upload_url, gcs_path, sanitized_filename } = initResponse.data;

    await putToGcs(upload_url, file, onProgress);

    const completeResponse = await client.POST(
      "/v1/cases/{case_id}/revisions/{revision_id}/uploads/zip/complete",
      {
        params: {
          path: { case_id: caseId, revision_id: revisionId },
        },
        body: { gcs_path, filename: sanitized_filename },
      }
    );

    if (completeResponse.error) {
      const msg = (completeResponse.error as any).message || "Failed to complete zip upload";
      throw new Error(`${file.name}: ${msg}`);
    }

    const data = completeResponse.data as any;
    if (data?.invalid_files?.length) {
      const lines = data.invalid_files.map(
        (f: { filename: string; reason: string }) => `${f.filename}: ${f.reason}`
      );
      throw new Error(`${file.name} has invalid files:\n${lines.join("\n")}`);
    }
  };

  return { uploadZip };
};

export const useFileUpload = () => {
  const [files, setFiles] = useState<(FileUpload | CaseFile)[]>([]);

  const { user } = useAuth();
  const { showError } = useError();
  const { client } = useApi();
  const { uploadZip } = useZipUpload();

  const finalize = async (
    caseId: string,
    revisionId: number,
    uploadSessionId: string
  ): Promise<string | undefined> => {
    if (!user) throw new Error("User not authenticated");
    const response = await client.POST(
      `/v1/cases/{case_id}/revisions/{revision_id}/uploads/complete`,
      {
        params: {
          path: {
            case_id: caseId,
            revision_id: revisionId,
          },
        },
        headers: {
          "X-Upload-Session-Id": uploadSessionId
        }
      }
    );

    if (response.error || response.data?.error) {
      const err: unknown = response.error ?? response.data?.error;
      // `data.error` is a backend enum code (UploadErrorCode), not user-facing
      // copy. Known codes are hidden behind a generic message and exposed via
      // `.code` for callers to branch on.
      const fallbackMessage = "Failed to finalize file upload";
      let message = fallbackMessage;
      let code: string | undefined;
      let filename: string | undefined;
      if (typeof err === "string") {
        if (UPLOAD_ERROR_CODE_SET.has(err)) {
          code = err;
        } else {
          message = err;
        }
      } else if (typeof err === "object" && err !== null) {
        if ("message" in err) {
          message = String((err as { message: unknown }).message);
        }
        if ("code" in err) {
          code = String((err as { code: unknown }).code);
        }
        if ("filename" in err) {
          filename = String((err as { filename: unknown }).filename);
        }
      }
      const finalizeError = new Error(message) as Error & { code?: string; filename?: string };
      finalizeError.code = code;
      finalizeError.filename = filename;
      throw finalizeError;
    }

    return response.data.message ?? undefined;
  };

  const start = async (
    caseId: string,
    revisionId: number,
    updatedFiles: (FileUpload | CaseFile)[],
    onProgress?: UploadProgressCallback
  ): Promise<FileUploader> => {
    const idToken = await user?.getIdToken();
    if (!idToken) throw new Error("Not authenticated");

    Caching.clear();

    const uploader = new FileUploader(caseId, revisionId, idToken, client);
    if (onProgress) uploader.setProgress(onProgress);

    const existingIds = (
      updatedFiles.filter((f) => "id" in f) as CaseFile[]
    ).map((f) => f.id);
    const newFiles = updatedFiles.filter((f) => !("id" in f)) as FileUpload[];

    try {
      await uploader.startSession(existingIds, newFiles);
    } catch (err) {
      if (uploader.sessionId) {
        await finalize(caseId, revisionId, uploader.sessionId).catch(() => {});
      }
      throw err;
    }
    await uploader.copyExisting();
    return uploader;
  };

  const uploadFiles = useCallback(
    async (
      caseId: string,
      revisionId: number,
      updatedFiles: (FileUpload | CaseFile)[],
      onProgress?: UploadProgressCallback
    ): Promise<void> => {
      const uploader = await start(caseId, revisionId, updatedFiles, onProgress);
      const sessionId = uploader.sessionId!;
      let uploadError: Error | undefined;
      try {
        await uploader.uploadNew();
      } catch (err) {
        uploadError = err instanceof Error ? err : new Error(String(err));
      }
      if (uploadError) {
        await finalize(caseId, revisionId, sessionId).catch(() => {});
        throw uploadError;
      }
      await finalize(caseId, revisionId, sessionId);
    },
    [user, showError]
  );

  const uploadAll = useCallback(
    async (
      caseId: string,
      revisionId: number,
      updatedFiles: (FileUpload | CaseFile)[],
      onProgress?: UploadProgressCallback
    ): Promise<{ finalizeMessage?: string }> => {
      const zipUploads = updatedFiles.filter(isNewZipUpload);
      const nonZipFiles = updatedFiles.filter((f) => !isNewZipUpload(f));

      const uploader = await start(caseId, revisionId, nonZipFiles, onProgress);
      const sessionId = uploader.sessionId!;

      const zipResults = await Promise.allSettled(
        zipUploads.map((f) => uploadZip(caseId, revisionId, f.file, onProgress))
      );
      const { zipErrorMessage } = collectZipOutcomes(zipResults);

      if (zipErrorMessage) {
        await finalize(caseId, revisionId, sessionId).catch(() => {});
        throw new Error(zipErrorMessage);
      }

      try {
        await uploader.uploadNew();
      } catch (err) {
        await finalize(caseId, revisionId, sessionId).catch(() => {});
        throw err;
      }
      
      let finalizeMessage: string | undefined;
      try {
        finalizeMessage = await finalize(caseId, revisionId, sessionId);
      } catch (err) {
        const e = err as Error & { filename?: string };
        throw new Error(e.filename ? `${e.filename}: ${e.message}` : e.message);
      }
      return { finalizeMessage };
    },
    [user, uploadZip, showError]
  );

  const fetchCaseFiles = useCallback(
    async (caseId: string, revisionId?: number): Promise<CaseFile[]> => {
      const response = await client.GET(
        "/v1/cases/{case_id}/revisions/{revision_id}/uploads/",
        {
          params: {
            path: {
              case_id: caseId,
              revision_id: revisionId ?? "latest",
            },
          },
          cacheable: true,
        }
      );
      return response.data as CaseFile[];
    },
    [client]
  );

  const presignCaseFiles = useCallback(
    async (
      caseId: string,
      fileIds: number[],
      revisionId?: number
    ): Promise<Record<number, string | null>> => {
      if (fileIds.length === 0) return {};
      const response = await client.POST(
        "/v1/cases/{case_id}/revisions/{revision_id}/uploads/presign",
        {
          params: {
            path: {
              case_id: caseId,
              revision_id: revisionId ?? "latest",
            },
          },
          body: { file_ids: fileIds },
        }
      );
      if (response.error) {
        throw new Error("Failed to generate presigned URLs");
      }
      const urls = (response.data as RevisionFilesPresignResponse)?.urls ?? [];
      return presignedUrlsToMap(urls);
    },
    [client]
  );

  return {
    files,
    fetchCaseFiles,
    presignCaseFiles,
    setFiles,
    uploadFiles,
    uploadAll,
  };
};

/**
 * Returns a `getPreviewUrl(file)` callback that presigns a single revision
 * file on demand. Resolves to undefined (after surfacing an error toast)
 * when the case is unknown or presigning fails.
 */
export const usePresignPreview = (caseId?: string, revisionId?: number) => {
  const { presignCaseFiles } = useFileUpload();
  const { showError } = useError();

  return useCallback(
    async (file: CaseFile): Promise<string | undefined> => {
      if (!caseId) return undefined;
      try {
        const urls = await presignCaseFiles(caseId, [file.id], revisionId);
        const url = urls[file.id];
        if (!url) showError("Failed to generate a preview link");
        return url ?? undefined;
      } catch {
        showError("Failed to generate a preview link");
        return undefined;
      }
    },
    [caseId, revisionId, presignCaseFiles, showError]
  );
};

export const useCaseManagement = () => {
  const { client } = useApi();
  const { showError, showSuccess } = useError();
  const navigate = useNavigate();
  
  const createCase = useCallback(async (): Promise<string> => {
    try {
      const requestPayload: CreateCaseRequest = {
        name: "",
      };
      const caseResponse = await client.POST("/v1/cases/", {
        body: requestPayload,
      });
      const caseResponseData = caseResponse.data as CreateCaseResponse;
      return caseResponseData.id;
    } catch (error) {
      throw new CreateCaseError("Failed to create case");
    }
  }, [client, showError]);

  const createRevision = useCallback(
    async (caseId: string): Promise<number> => {
      const createRevisionPayload: CreateCaseRevisionRequest = {
        description: "Initial case revision",
      };
      const revisionResponse = await client.POST(
        `/v1/cases/{case_id}/revisions/`,
        {
          params: {
            path: {
              case_id: caseId,
            },
          },
          body: createRevisionPayload,
        }
      );

      if (revisionResponse.data) {
        return (revisionResponse.data as CreateCaseRevisionResponse).id;
      }
      throw new CreateRevisionError(
        "Failed to create case revision, please try again"
      );
    },
    [client, showError]
  );

  return {
    createCase,
    createRevision,
    showSuccess,
    navigate,
  };
};
